import { useEffect } from 'react';

/**
 * Content Protection Hook
 * Prevents right-click, keyboard save/print/devtools shortcuts,
 * drag-and-drop of assets, and disables screen capture via
 * the Screen Capture API where supported.
 *
 * NOTE: OS-level screenshots (PrintScreen, macOS Cmd+Shift+3/4)
 * and external screen recorders cannot be blocked in a browser.
 * This provides strong browser-level deterrents.
 */
const useContentProtection = (): void => {
  useEffect(() => {
    // ── 1. Disable right-click context menu ─────────────────────────────
    const handleContextMenu = (e: MouseEvent): boolean => {
      e.preventDefault();
      return false;
    };

    // ── 2. Block keyboard shortcuts ──────────────────────────────────────
    const handleKeyDown = (e: KeyboardEvent): boolean | void => {
      const target = e.target as HTMLElement;
      const isInput = target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      const ctrl = e.ctrlKey || e.metaKey;

      // Always block these regardless of focus
      const alwaysBlock =
        (ctrl && e.key === 's') ||                                    // Save page
        (ctrl && e.key === 'p') ||                                    // Print
        (ctrl && e.key === 'u') ||                                    // View source
        (ctrl && e.shiftKey && ['i', 'I', 'c', 'C', 'j', 'J', 'k', 'K'].includes(e.key)) || // DevTools
        e.key === 'F12' ||                                            // DevTools
        e.key === 'PrintScreen';                                      // Screenshot key

      // Block copy/select-all only outside editable fields
      const blockOutsideInput = !isInput && (
        (ctrl && e.key === 'c') ||   // Copy
        (ctrl && e.key === 'a') ||   // Select all
        (ctrl && e.key === 'x')      // Cut
      );

      if (alwaysBlock || blockOutsideInput) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    // ── 3. Disable drag-and-drop of images and media ─────────────────────
    const handleDragStart = (e: DragEvent): boolean => {
      e.preventDefault();
      return false;
    };

    // ── 4. Block copy event outside input fields ─────────────────────────
    const handleCopy = (e: ClipboardEvent): boolean | void => {
      const target = e.target as HTMLElement;
      const isInput = target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;
      if (!isInput) {
        e.preventDefault();
        return false;
      }
    };

    // ── 5. Block cut event outside input fields ──────────────────────────
    const handleCut = (e: ClipboardEvent): boolean | void => {
      const target = e.target as HTMLElement;
      const isInput = target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;
      if (!isInput) {
        e.preventDefault();
        return false;
      }
    };

    // ── 6. Request Screen Capture API restriction (where supported) ───────
    // This is a best-effort hint to the browser; not universally supported.
    const applyScreenCaptureRestriction = () => {
      const style = (document.body.style as any);
      if ('webkitUserModify' in style) {
        style.webkitUserModify = 'read-only';
      }
    };
    applyScreenCaptureRestriction();

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('cut', handleCut);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('cut', handleCut);
    };
  }, []);
};

export default useContentProtection;
